/**
 * AtlasFestivalModule — SEASONAL FESTIVALS, the client (2026-09-24). Rules in lib/festival_catalog.ts (mirrored);
 * the server stages them (festival_ops) and judges them (reducers/festivals.ts).
 *
 *   THE TOWN TAB "Festivals" (first): the season and its festival, when it opens on your clock, the ones running now
 *     nearest first (waypoint to them), your time at the one you stand in and the keepsake, the contest — what it asks,
 *     the standings, and how to enter from your own stacks (or, on Lantern Night, a rocket from where you stand) —
 *     and your keepsake collection.
 *   AT A FESTIVAL (within FESTIVAL_ATTEND_M of one running): a chip under the wallet and a `festivalPresence` ping
 *     every PING_S (THE DUTY LAW on the server: only time spent there counts); the keepsake lands at five minutes.
 * Levers: `__festival()`.
 */
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import {
  FESTIVALS, FESTIVAL_ATTEND_M, FESTIVAL_KEEPSAKE_S, FESTIVAL_PRIZES, FESTIVAL_MIN_ENTRANTS, FESTIVAL_SEASONS, festivalWindowAt, nextFestival,
  type FestivalSeason,
} from '../lib/festival_catalog.js';
import { townName } from '../lib/town_names.js';
import { el, btn, input, select, head, chip, muted, card, act, debounced, ms } from './town/town_ui.js';
import { registerTownTab } from './town/town_tabs.js';
import { softRender, fieldByKey } from '../lib/soft_render.js';

interface FestRow { key: string; epoch: number; season: string; settlementId: bigint; eventId: bigint; x: number; z: number; startsAt: { microsSinceUnixEpoch: bigint }; endsAt: { microsSinceUnixEpoch: bigint }; resolved: boolean }
interface AttendRow { key: string; epoch: number; settlementId: bigint; identity: Identity; seconds: number; keepsake: boolean }
interface EntryRow { key: string; festivalKey: string; identity: Identity; name: string; score: number; label: string; place: number }
interface KeepsakeRow { identity: Identity; season: string; count: number }
interface StackRow { stackId: bigint; owner: Identity; familyKey: string; name: string; statsJson: string; qty: number }

const PING_S = 20;
const us = (): bigint => BigInt(Date.now()) * 1000n;
const clock = (msAbs: number): string => new Date(msAbs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const mmss = (s: number): string => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export class AtlasFestivalModule implements IModule {
  readonly name = 'AtlasFestival';
  private subbed = false;
  private tabEl: HTMLElement | null = null;
  private chipEl: HTMLDivElement | null = null;
  private pingAt = 0;
  private here: FestRow | null = null;
  private scanAt = 0;
  private lastPingError = '';
  private readonly rerender = debounced(() => { if (this.tabEl?.isConnected) this.renderTab(this.tabEl); }, 300);

  init(app: App): void {
    registerTownTab({ key: 'festivals', label: 'Festivals', order: 0, render: (h) => { this.tabEl = h; this.renderTab(h); } });
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    app.world.addSystem(makeSystem('Festival', Phase.UI, 34, () => this.tick()));
    const c = document.createElement('div');
    c.id = 'festival-chip';
    c.className = 'ax-chip';
    c.style.cssText = 'position:fixed;top:92px;left:50%;transform:translateX(-50%);z-index:31;display:none;pointer-events:none;'
      + 'font:12px var(--ax-font);color:var(--ax-accent);background:color-mix(in srgb, var(--ax-bg) 80%, transparent);padding:4px 10px';
    document.body.appendChild(c);
    this.chipEl = c;
    (window as unknown as { __festival?: unknown }).__festival = () => ({
      here: this.here ? { key: this.here.key, season: this.here.season, sid: String(this.here.settlementId) } : null,
      running: this.running().map((r) => ({ key: r.key, sid: String(r.settlementId), d: Math.round(Math.hypot(r.x - atlas.localX, r.z - atlas.localZ)) })).slice(0, 5),
      attendance: this.myAttendance(this.here?.key ?? '')?.seconds ?? 0,
      keepsakes: this.keepsakes(),
      lastPingError: this.lastPingError,
      next: (() => { const n = nextFestival(us()); return { season: n.season, startsMs: Number(n.startUs / 1000n) }; })(),
    });
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    const db = conn.db as unknown as Record<string, { onInsert?(cb: () => void): void; onUpdate?(cb: () => void): void; onDelete?(cb: () => void): void } | undefined>;
    for (const n of ['seasonFestival', 'festivalAttendance', 'festivalEntry', 'festivalKeepsake']) {
      const t = db[n];
      t?.onInsert?.(() => this.rerender()); t?.onUpdate?.(() => this.rerender()); t?.onDelete?.(() => this.rerender());
    }
    try {
      conn.subscriptionBuilder().subscribe([
        'SELECT * FROM season_festival', 'SELECT * FROM festival_attendance', 'SELECT * FROM festival_entry', 'SELECT * FROM festival_keepsake',
      ]);
    } catch { /* pre-publish */ }
  }

  private rows<T>(name: string): T[] {
    try { const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)?.[name]; return t ? [...t.iter()] : []; }
    catch { return []; }
  }
  private isMe(id: Identity): boolean { return atlas.identityHex !== null && id.toHexString() === atlas.identityHex; }

  private running(): FestRow[] {
    const now = Date.now();
    return this.rows<FestRow>('seasonFestival')
      .filter((r) => !r.resolved && ms(r.startsAt) <= now && ms(r.endsAt) > now)
      .sort((a, b) => Math.hypot(a.x - atlas.localX, a.z - atlas.localZ) - Math.hypot(b.x - atlas.localX, b.z - atlas.localZ));
  }
  private myAttendance(festKey: string): AttendRow | null {
    for (const a of this.rows<AttendRow>('festivalAttendance')) if (this.isMe(a.identity) && a.key.startsWith(`${festKey}:`)) return a;
    return null;
  }
  private entriesOf(festKey: string): EntryRow[] {
    return this.rows<EntryRow>('festivalEntry').filter((e) => e.festivalKey === festKey).sort((a, b) => b.score - a.score);
  }
  private keepsakes(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const k of this.rows<KeepsakeRow>('festivalKeepsake')) if (this.isMe(k.identity)) out[k.season] = k.count;
    return out;
  }

  private tick(): void {
    if (atlas.currentWorldId === null || atlas.spaceFlight) { this.setChip(''); return; }
    const now = performance.now();
    if (now - this.scanAt > 1000) {
      this.scanAt = now;
      const r = this.running()[0] ?? null;
      this.here = r && Math.hypot(r.x - atlas.localX, r.z - atlas.localZ) <= FESTIVAL_ATTEND_M ? r : null;
    }
    const f = this.here;
    if (!f) { this.setChip(''); return; }
    if (now - this.pingAt > PING_S * 1000) {
      this.pingAt = now;
      const red = atlas.conn?.reducers as unknown as { festivalPresence?(a: Record<string, never>): Promise<void> } | undefined;
      void red?.festivalPresence?.({})?.then(() => { this.lastPingError = ''; }).catch((e: Error) => { this.lastPingError = String(e?.message ?? e); });
    }
    const def = FESTIVALS[f.season as FestivalSeason];
    const att = this.myAttendance(f.key);
    const secs = att?.seconds ?? 0;
    const left = Math.max(0, (ms(f.endsAt) - Date.now()) / 1000);
    const keep = att?.keepsake ? `${def.keepsake} earned` : `keepsake at ${mmss(FESTIVAL_KEEPSAKE_S)} (${mmss(secs)})`;
    this.setChip(`${cap(def.name)} in ${townName(f.settlementId)} — ${keep} · ${mmss(left)} left`);
  }
  private setChip(text: string): void {
    const c = this.chipEl;
    if (!c) return;
    if (!text) { if (c.style.display !== 'none') c.style.display = 'none'; return; }
    if (c.textContent !== text) c.textContent = text;
    if (c.style.display !== 'block') c.style.display = 'block';
  }

  // ── the tab ────────────────────────────────────────────────────────────────
  // SOFT RENDER (2026-09-25): built into a scratch and patched over the live host — the Town window no longer rebuilds
  // under a contest quantity being typed (lib/soft_render.ts).
  private renderTab(real: HTMLElement): void { softRender(real, (h) => this.buildTab(h)); }
  private buildTab(host: HTMLElement): void {
    const nowUs = us();
    const w = festivalWindowAt(nowUs);
    const def = FESTIVALS[w.season];
    host.appendChild(head(`${cap(def.name)} — the festival of ${w.season === 'highsun' ? 'High Sun' : `the ${cap(w.season)}`}`));
    host.appendChild(muted(`${cap(def.blurb)}. The year here is a day: four seasons of six hours, and each season's festival fills every city and town square in its second hour. Stay five minutes for its keepsake (${def.keepsake}); the contest pays the top three ${FESTIVAL_PRIZES.join(' / ')} ⓒ when it ends — if at least ${FESTIVAL_MIN_ENTRANTS} guests entered.`));
    const running = this.running();
    if (running.length === 0) {
      const n = nextFestival(nowUs);
      const at = Number(n.startUs / 1000n);
      host.appendChild(card()).appendChild(el('div', '', `Next: ${cap(FESTIVALS[n.season].name)} opens at ${clock(at)} (in ${Math.max(1, Math.round((at - Date.now()) / 60_000))} min) and runs an hour.`));
    } else {
      host.appendChild(head('Running now'));
      for (const r of running.slice(0, 6)) host.appendChild(this.festCard(r));
    }
    // The contest at the festival I stand in.
    if (this.here) host.appendChild(this.contestCard(this.here));
    // My keepsakes.
    const ks = this.keepsakes();
    const k = card();
    k.appendChild(el('div', 'font-weight:600', 'Your keepsakes'));
    const line = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    for (const s of FESTIVAL_SEASONS) line.appendChild(chip(`${FESTIVALS[s].keepsake} × ${ks[s] ?? 0}`, (ks[s] ?? 0) > 0 ? 'good' : ''));
    k.appendChild(line);
    host.appendChild(k);
  }

  private festCard(r: FestRow): HTMLDivElement {
    const c = card();
    c.setAttribute('data-row', `fest:${r.key}`); // rows pair by identity in the soft render
    const def = FESTIVALS[r.season as FestivalSeason];
    const d = Math.round(Math.hypot(r.x - atlas.localX, r.z - atlas.localZ));
    c.appendChild(el('div', 'font-weight:600', `${cap(def.name)} — ${townName(r.settlementId)}`));
    const row = el('div', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap');
    row.appendChild(chip(d <= FESTIVAL_ATTEND_M ? 'you are here' : `${d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${d} m`} away`, d <= FESTIVAL_ATTEND_M ? 'good' : 'info'));
    row.appendChild(chip(`until ${clock(ms(r.endsAt))}`));
    const att = this.myAttendance(r.key);
    if (att) row.appendChild(chip(att.keepsake ? 'keepsake earned' : `${mmss(att.seconds)} attended`, att.keepsake ? 'good' : ''));
    if (d > FESTIVAL_ATTEND_M) row.appendChild(btn('Waypoint', () => void act(atlas.conn?.reducers.createWaypoint({ name: `${def.key === 'lantern_night' ? 'Lanterns' : 'Festival'}: ${townName(r.settlementId)}`.slice(0, 32), posX: r.x, posZ: r.z }), 'Waypoint set')));
    c.appendChild(row);
    const top = this.entriesOf(r.key).slice(0, 3);
    if (top.length) c.appendChild(muted(`Leading: ${top.map((e, i) => `${i + 1}. ${e.name} (${e.label})`).join(' · ')}`));
    return c;
  }

  private contestCard(r: FestRow): HTMLDivElement {
    const c = card();
    const def = FESTIVALS[r.season as FestivalSeason];
    c.appendChild(el('div', 'font-weight:600', `The contest: ${def.contestLabel}`));
    const mine = this.entriesOf(r.key).find((e) => this.isMe(e.identity));
    if (mine) c.appendChild(muted(`Your entry: ${mine.label}`));
    const conn = atlas.conn;
    if (!conn) return c;
    if (def.contest === 'fireworks') {
      c.appendChild(muted('Every rocket you launch here lights a lantern for your score (a show counts five).'));
      const row = el('div', 'display:flex;gap:8px;flex-wrap:wrap');
      row.appendChild(btn('Launch a rocket (5 ⓒ)', () => void act(conn.reducers.launchFirework({ kind: 'peony', palette: Math.floor(Math.random() * 8) }), 'A lantern for the night')));
      row.appendChild(btn('Launch a show (60 ⓒ)', () => void act(conn.reducers.launchFireworkShow({}), 'Your show lights the square')));
      c.appendChild(row);
      return c;
    }
    const fams = def.contest === 'donate' ? ['grain', 'produce'] : def.contest === 'fish' ? ['brine_catch'] : ['produce'];
    const stacks = this.rows<StackRow>('playerResource').filter((s) => this.isMe(s.owner) && fams.includes(s.familyKey) && s.qty > 0);
    if (stacks.length === 0) {
      c.appendChild(muted(def.contest === 'fish' ? 'You have no fish you landed yourself — cast a line at any water (E at the shore) and come back.' : `You carry no ${fams.join(' or ')} to enter.`));
      return c;
    }
    const pick = select(stacks.map((s) => {
      let extra = '';
      try { const st = JSON.parse(s.statsJson) as Record<string, number>; if (def.contest === 'fish' && st.LEN) extra = ` — ${Math.round(st.LEN)} cm`; } catch { /* legacy */ }
      return { value: String(s.stackId), label: `${s.name} ×${s.qty}${extra}` };
    }));
    const qty = input('qty', '5', 'number', '70px');
    pick.setAttribute('data-k', `contest-pick:${r.key}`);
    qty.setAttribute('data-k', `contest-qty:${r.key}`);
    const row = el('div', 'display:flex;gap:8px;align-items:center;flex-wrap:wrap');
    row.appendChild(pick);
    if (def.contest === 'donate') row.appendChild(qty);
    row.appendChild(btn(def.contest === 'donate' ? 'Give to the town' : def.contest === 'fish' ? 'Weigh it' : 'Enter it', () => {
      // The fields ON SCREEN (2026-09-25): a kept field holds the player's pick and quantity.
      const livePick = fieldByKey<HTMLSelectElement>(this.tabEl, `contest-pick:${r.key}`) ?? pick;
      const liveQty = fieldByKey(this.tabEl, `contest-qty:${r.key}`) ?? qty;
      const n = def.contest === 'donate' ? Math.max(1, Math.min(10_000, Math.floor(Number(liveQty.value) || 1))) : 1;
      void act(conn.reducers.enterFestivalContest({ stackId: BigInt(livePick.value), qty: n }), def.contest === 'donate' ? 'Given — the town thanks you' : 'Entered', () => this.rerender());
    }, true));
    c.appendChild(row);
    return c;
  }

  dispose(): void { this.chipEl?.remove(); }
}

